import styles from './navigator.module.scss';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { FC, useContext, useState, useEffect } from 'react';
import NavButton from '../../molecules/nav-button/nav-button';
import useNavigation from '../../../custom-hooks/useNavigation';
import {
  WAccordion,
  WAccordionDetails,
  WAccordionSummary,
  WList,
  WListItemText,
} from '../../wrappers/wrapper';
import Logo from 'components/molecules/logo/logo';
import { CommonContext } from 'contexts/commonContext';
import { useNavigate, useParams } from 'react-router-dom';
import { UserContext } from 'contexts/userContext';
import { Divider } from '@mui/material';
import useDeviceType from 'custom-hooks/useDeviceType';
import Notifications from '../notifications/notifications';
import SearchBar from '../search-bar/search-bar';
import { localStorageService, STORAGE_KEY } from 'services/local-storage.service';

interface NavigatorProps {
  className?: string;
}
const Navigator: FC<NavigatorProps> = ({ className }) => {
  const navigation = useNavigation();
  const navigate = useNavigate();

  const { isDesktop } = useDeviceType();
  const { article_id, userId } = useParams();

  const { setShowMenu } = useContext(CommonContext);
  const { user, currentPage, setCurrentPage } = useContext(UserContext);

  const getExpandedItems = () => {
    return navigation
      .filter((item) => item.kind === 'header' && item.title)
      .map((item) => item.title as string);
  };

  const [expanded, setExpanded] = useState<string[]>(getExpandedItems());

  const hideActiveTab = article_id || userId;


  const handleClick = (item: any) => {
    const { title, icon, path } = item;
    setCurrentPage({ title: title, icon: icon });
    localStorageService.setItem(STORAGE_KEY.CURRENT_PAGE, JSON.stringify({ title, icon }));
    navigate(path);
    setShowMenu(false);
  };

  useEffect(() => {
    const savedPage = localStorageService.getItem(STORAGE_KEY.CURRENT_PAGE);
    if (savedPage) {
      setCurrentPage(JSON.parse(savedPage));
    }
  }, []);

  const handleAccordionChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded((prevExpanded) => {
        const newExpanded = isExpanded
          ? [...prevExpanded, panel]
          : prevExpanded.filter((exp) => exp !== panel);
        localStorageService.setItem(STORAGE_KEY.EXPANDED_ITEMS, JSON.stringify(newExpanded));
        return newExpanded;
      });
    };

  useEffect(() => {
    const savedExpanded = localStorageService.getItem(STORAGE_KEY.EXPANDED_ITEMS);
    if (savedExpanded) {
      setExpanded(JSON.parse(savedExpanded));
    }
  }, []);

  const renderNavItems = (items: any[]) => {
    return items.map((item, index) => {
      if (item.kind === 'header') {
        return (
          <WAccordion
            key={index}
            expanded={expanded.includes(item.title)}
            onChange={handleAccordionChange(item.title)}
            className={styles.accordion}
            sx={{
              boxShadow: 'none',
              border: 'none',
              '&:before': { display: 'none' },
            }}
          >
            <WAccordionSummary
              expandIcon={<ExpandMoreIcon className={styles.expandIcon} />}
              className={styles.summary}
            >
              <WListItemText primary={item.title} />
            </WAccordionSummary>
            <WAccordionDetails className={styles.details}>
              <WList className={styles.list}>
                {item.children && renderNavItems(item.children)}
              </WList>
            </WAccordionDetails>
          </WAccordion>
        );
      }
      return (
        <NavButton
          key={index}
          icon={item.icon}
          title={item.title}
          isActive={!hideActiveTab && currentPage.title === item.title}
          onClick={() => handleClick(item)}
        />
      );
    });
  };

  return (
    <div className={`${styles.navigator} ${className || ''}`}>
      <Logo className={styles.logo} />
      {!isDesktop && (
        <>
          <div className={styles.actionButtons}>
            <SearchBar />
            {!!user._id && <Notifications />}
          </div>
          <Divider style={{border:"var(--border-light)"}} />
        </>
      )}
      <WList>{renderNavItems(navigation)}</WList>
    </div>
  );
};

export default Navigator;
